/**
 * The long moves' motions (ท่ายาว, design docs/design/combat/2026-10-11-long-moves.md §4.3): every art's caster motions
 * (motions/long/<art>.ts, keyed by the rules' beat `pose` / `tellPose` / `whiff.pose` names) and the held foe's receive
 * motions (motions/long/receive.ts). The motion registry (motions/index.ts) lists this one group; a new art adds its file
 * to the three lists below.
 *
 * The client side of a long move asks two things each frame (the rules' `longBeat` events + the beat table give the beat
 * and how far through it):
 * - `longCasterFrame(pose, k)`: the caster's frame for a beat's pose key (null: no motion for it, play the move's swing);
 *   `turn` is in 45° steps from the heading held at the catch (toward the foe), `lift` art px, `rot` radians.
 * - `longFoeFrame(beats, i, k)` (receive.ts): the held foe's.
 * Beat → motion table for bs-tempest: docs/research/TEMPEST_MOTION.md.
 */
import type { LimbSpec } from '../kit';
import type { LongFrame } from './kit';
import { channelAt, type LongChannels } from './kit';
import * as breezeSword from './breezeSword';
import * as taijiFist from './taijiFist';
import * as bloodClaw from './bloodClaw';
import * as receive from './receive';

export { longFoeFrame, foeMotionOf, LIFT_PX_PER_M, type FoeBeat } from './receive';
export type { LongFrame } from './kit';

/** Every long-move key pose, by name. */
export const POSES = { ...breezeSword.POSES, ...taijiFist.POSES, ...bloodClaw.POSES, ...receive.POSES };
/** Every long-move weapon arm. */
export const ARMS = { ...breezeSword.ARMS, ...taijiFist.ARMS, ...bloodClaw.ARMS, ...receive.ARMS } satisfies Record<string, LimbSpec>;
/** Every long-move motion (appended to the registry's list: their place is their phase). */
export const MOTIONS = [...breezeSword.MOTIONS, ...taijiFist.MOTIONS, ...bloodClaw.MOTIONS, ...receive.MOTIONS] as const;

const CASTER: ReadonlyMap<string, { ease: readonly string[]; ch: LongChannels }> = new Map(
  [
    ...breezeSword.MOTIONS.map((m) => [m.id, { ease: m.ease, ch: breezeSword.CHANNELS[m.id] }] as const),
    ...taijiFist.MOTIONS.map((m) => [m.id, { ease: m.ease, ch: taijiFist.CHANNELS[m.id] }] as const),
    ...bloodClaw.MOTIONS.map((m) => [m.id, { ease: m.ease, ch: bloodClaw.CHANNELS[m.id] }] as const),
  ],
);

/** The caster's frame for the beat pose key `pose`, `k` (0–1) of the way through the beat (null: no motion for it). */
export function longCasterFrame(pose: string, k: number): LongFrame | null {
  const m = CASTER.get(pose);
  if (!m) return null;
  const u = Math.max(0, Math.min(1, k));
  const ease = m.ease as Parameters<typeof channelAt>[1];
  return {
    id: pose,
    u,
    lift: channelAt(m.ch.lift, ease, u),
    rot: channelAt(m.ch.rot, ease, u),
    turn: Math.round(channelAt(m.ch.turn, ease, u)),
  };
}

/** The pose keys that have a caster motion (for the coverage check, design §4.4). */
export const LONG_POSE_KEYS: readonly string[] = [...CASTER.keys()];
